import type { ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { FormSection } from '@/components/ui/FormPage';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import {
  DEFAULT_TELEPHONE_CODE,
  MEDCOM_JOB_TITLE_MAX,
  MEDCOM_MAX_TELEPHONE_SUBSCRIBERS,
  MEDCOM_TELEPHONE_CODES,
  MEDCOM_TELEPHONE_IDENTIFIER_MAX,
  emptyTelephoneDraft,
  stamkortFieldErrorTexts,
  type MedcomTelephoneCode,
  type StamkortFieldMessages,
  type TelephoneSubscriberDraft,
} from '@/lib/patientStamkortFields';
import { FORM_ERROR_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/**
 * `cards` is Ny patient: each block is its own FormSection, same chrome as
 * Grundoplysninger, CPR and Adresse. `sections` is Rediger patient: the same
 * two blocks, with the slide-over's h3 section headings.
 */
export type PatientStamkortPresentation = 'cards' | 'sections';

type PatientStamkortFieldsProps = {
  presentation: PatientStamkortPresentation;
  jobTitle: string;
  onJobTitleChange: (value: string) => void;
  subscribers: TelephoneSubscriberDraft[];
  onSubscribersChange: (value: TelephoneSubscriberDraft[]) => void;
  submitted: boolean;
};

function StamkortBlock({
  presentation,
  title,
  children,
}: {
  presentation: PatientStamkortPresentation;
  title: string;
  children: ReactNode;
}) {
  switch (presentation) {
    case 'cards':
      return <FormSection title={title}>{children}</FormSection>;
    case 'sections':
      return (
        <section>
          <h3 className={cn(TYPE.section, 'mb-s4 text-ds-text')}>{title}</h3>
          {children}
        </section>
      );
    default: {
      const unknown: never = presentation;
      return unknown;
    }
  }
}

function telephoneTypeLabel(
  code: MedcomTelephoneCode,
  labels: Record<MedcomTelephoneCode, string>,
): string {
  switch (code) {
    case 'fastnet':
      return labels.fastnet;
    case 'mobil':
      return labels.mobil;
    case 'arbejde':
      return labels.arbejde;
    default: {
      const unknown: never = code;
      return unknown;
    }
  }
}

export function PatientStamkortFields({
  presentation,
  jobTitle,
  onJobTitleChange,
  subscribers,
  onSubscribersChange,
  submitted,
}: PatientStamkortFieldsProps) {
  const { t } = useTranslation();
  const messages: StamkortFieldMessages = {
    jobTitleTooLong: t('patients.errors.job_title_length'),
    invalidPhone: t('patients.errors.invalid_phone'),
    typeRequired: t('patients.errors.telephone_type_required'),
    identifierTooLong: t('patients.errors.telephone_identifier_length'),
    tooMany: t('patients.errors.too_many_telephones'),
  };
  const typeLabels: Record<MedcomTelephoneCode, string> = {
    fastnet: t('patients.telephone_types.fastnet'),
    mobil: t('patients.telephone_types.mobil'),
    arbejde: t('patients.telephone_types.arbejde'),
  };
  const errors = stamkortFieldErrorTexts(submitted, jobTitle, subscribers, messages);
  const atLimit = subscribers.length >= MEDCOM_MAX_TELEPHONE_SUBSCRIBERS;

  const updateRow = (index: number, patch: Partial<TelephoneSubscriberDraft>) => {
    onSubscribersChange(
      subscribers.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    );
  };

  return (
    <div className="flex flex-col gap-s5">
      <StamkortBlock presentation={presentation} title={t('patients.field_job_title')}>
        {/* The heading is this field's name, so the input is not labeled again. */}
        <Input
          aria-label={t('patients.field_job_title')}
          name="job_title"
          value={jobTitle}
          maxLength={MEDCOM_JOB_TITLE_MAX}
          onChange={(event) => onJobTitleChange(event.target.value)}
          hint={t('patients.field_job_title_hint')}
          error={errors.jobTitle}
        />
      </StamkortBlock>

      <StamkortBlock presentation={presentation} title={t('patients.section_telephones')}>
        <div className="space-y-s2">
          {subscribers.map((row, index) => {
            const rowError = errors.subscribers[index];
            const selectId = `patient-telephone-type-${index}`;
            return (
              <div key={index} className="flex items-start gap-s2">
                <div className="grid flex-1 gap-s4 nav:grid-cols-2">
                  <Input
                    label={t('patients.field_telephone_number')}
                    name={`telephone_subscriber_${index}`}
                    type="tel"
                    value={row.identifier}
                    maxLength={MEDCOM_TELEPHONE_IDENTIFIER_MAX}
                    onChange={(event) => {
                      const identifier = event.target.value;
                      updateRow(index, {
                        identifier,
                        ...(row.code === '' && identifier.trim().length > 0
                          ? { code: DEFAULT_TELEPHONE_CODE }
                          : {}),
                      });
                    }}
                    error={rowError?.identifier}
                  />
                  <Select
                    id={selectId}
                    label={t('patients.field_telephone_type')}
                    value={row.code}
                    onChange={(event) =>
                      updateRow(index, {
                        code: event.target.value as MedcomTelephoneCode | '',
                      })
                    }
                    error={rowError?.code}
                  >
                    <option value="">{t('patients.telephone_type_placeholder')}</option>
                    {MEDCOM_TELEPHONE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {telephoneTypeLabel(code, typeLabels)}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-7"
                  onClick={() =>
                    onSubscribersChange(subscribers.filter((_, rowIndex) => rowIndex !== index))
                  }
                  aria-label={t('patients.telephone_remove')}
                  title={t('patients.telephone_remove')}
                >
                  <X />
                </Button>
              </div>
            );
          })}

          {errors.tooMany && <p className={FORM_ERROR_TYPE}>{errors.tooMany}</p>}

          <Button
            type="button"
            variant="tertiary"
            onClick={() => onSubscribersChange([...subscribers, emptyTelephoneDraft()])}
            disabled={atLimit}
          >
            <Plus className="h-4 w-4" />
            {t('patients.telephone_add')}
          </Button>
        </div>
      </StamkortBlock>
    </div>
  );
}

export function stamkortMessagesFromT(
  t: (key: string) => string,
): StamkortFieldMessages {
  return {
    jobTitleTooLong: t('patients.errors.job_title_length'),
    invalidPhone: t('patients.errors.invalid_phone'),
    typeRequired: t('patients.errors.telephone_type_required'),
    identifierTooLong: t('patients.errors.telephone_identifier_length'),
    tooMany: t('patients.errors.too_many_telephones'),
  };
}
